import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export interface AppointmentDateInput {
  startDate?: string | null;
  startTime?: string | null;
  start?: string | Date | null;
  end?: string | Date | null;
}

const APPOINTMENT_DATE_FORMATS = ['MM-DD-YYYY', 'M-D-YYYY', 'MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD'];

const APPOINTMENT_DATETIME_FORMATS = [
  'MM-DD-YYYY h:mm A',
  'MM-DD-YYYY hh:mm A',
  'M-D-YYYY h:mm A',
  'M-D-YYYY hh:mm A',
  'MM/DD/YYYY h:mm A',
  'MM/DD/YYYY hh:mm A',
  'M/D/YYYY h:mm A',
  'M/D/YYYY hh:mm A',
  'YYYY-MM-DD h:mm A',
  'YYYY-MM-DD hh:mm A',
  'YYYY-MM-DD HH:mm'
];

const invalidDayjs = (): dayjs.Dayjs => dayjs(new Date(NaN));

// dayjs's `utc` plugin types only declare a 2-arg overload for dayjs.utc(),
// but customParseFormat's `strict` flag is honored at runtime through the
// shared parsing pipeline regardless of which factory is used.
const dayjsUtcStrict = dayjs.utc as unknown as (
  value: string,
  format: string,
  strict: boolean
) => dayjs.Dayjs;

const parseWithFormats = (value: string, formats: string[], strict: boolean): dayjs.Dayjs | null => {
  for (const format of formats) {
    const parsed = dayjsUtcStrict(value, format, strict);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
};

export const hasStructuredAppointmentDate = (appointment?: AppointmentDateInput | null): boolean =>
  Boolean(appointment?.startDate && appointment?.startTime);

export const getAppointmentStartMoment = (appointment?: AppointmentDateInput | null): dayjs.Dayjs => {
  if (!appointment) {
    return invalidDayjs();
  }

  if (appointment.startDate && appointment.startTime) {
    const combinedDateTime = `${appointment.startDate} ${appointment.startTime}`;

    const strictStructuredDateTime = parseWithFormats(combinedDateTime, APPOINTMENT_DATETIME_FORMATS, true);
    if (strictStructuredDateTime) {
      return strictStructuredDateTime;
    }

    const relaxedStructuredDateTime = parseWithFormats(combinedDateTime, APPOINTMENT_DATETIME_FORMATS, false);
    if (relaxedStructuredDateTime) {
      return relaxedStructuredDateTime;
    }
  }

  if (appointment.startDate) {
    const strictStructuredDate = parseWithFormats(appointment.startDate, APPOINTMENT_DATE_FORMATS, true);
    if (strictStructuredDate) {
      return strictStructuredDate;
    }

    const relaxedStructuredDate = parseWithFormats(appointment.startDate, APPOINTMENT_DATE_FORMATS, false);
    if (relaxedStructuredDate) {
      return relaxedStructuredDate;
    }
  }

  return dayjs.utc(appointment.start);
};

export const getAppointmentEndMoment = (appointment?: AppointmentDateInput | null): dayjs.Dayjs => {
  // If we have structured date/time, the end is not reliable - return invalid
  // This signals that we should not show a date range
  if (appointment?.startDate || appointment?.startTime) {
    return invalidDayjs();
  }

  return dayjs.utc(appointment?.end);
};
